import { motion } from "framer-motion";
import {
  Sparkles,
  Coins,
  HeartHandshake,
  Briefcase,
  ShieldCheck,
  Gem,
} from "lucide-react";

const SERVICES = [
  {
    id: "health",
    title: "Health Betterment & Vitality",
    desc: "Alchemical herbal remedies and bio-energetic cleansing drawn from Paracelsian hermetic texts to rejuvenate physical and spiritual vigor.",
    badge: "Hermetic Medicine",
    icon: Sparkles,
    span: "md:col-span-4",
    img: "https://images.unsplash.com/photo-1649301376803-1b7661759b7b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwzfHxteXN0aWNhbCUyMGdyaW1vaXJlJTIwY2FuZGxlcyUyMGNlbGVzdGlhbCUyMGNoYXJ0JTIwYW5jaWVudCUyMG1hZ2ljfGVufDB8fHxibGFja3wxNzkyNTAzNDYxfDA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "wealth",
    title: "Wealth Attraction & Prosperity",
    desc: "Picatrix astrological talismanic magic and planetary alignment rituals for financial abundance and enterprise expansion.",
    badge: "Picatrix Astrological",
    icon: Coins,
    span: "md:col-span-2",
  },
  {
    id: "love",
    title: "Love Life & Attraction",
    desc: "Ancient folk magic and Venusian rituals to dissolve heartaches, attract soul matches, and rekindle passion.",
    badge: "Folk Magic & Venus",
    icon: HeartHandshake,
    span: "md:col-span-2",
  },
  {
    id: "job",
    title: "Job Satisfaction & Career Arcana",
    desc: "Solomonic sigil invocation for authority, professional favor, promotions, and overcoming workplace barriers.",
    badge: "Solomonic Favor",
    icon: Briefcase,
    span: "md:col-span-4",
    img: "https://images.unsplash.com/photo-1674513000431-07beb07668d9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxjcnlzdGFsJTIwYmFsbCUyMGFsY2hlbXklMjBwb3Rpb24lMjBzdGFycyUyMG5pZ2h0JTIwc2t5JTIwdmludGFnZSUyMHBhcmNobWVudCUyMG1hZ2ljfGVufDB8fHx8MTc5MjUwMzQ2MXww&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "property",
    title: "Property Dispute Warding",
    desc: "Earth-bound warding rituals and binding spells from ancient grimoires to safeguard lands and settle property conflicts.",
    badge: "Grimoire Warding",
    icon: ShieldCheck,
    span: "md:col-span-3",
  },
  {
    id: "marriage",
    title: "Marriage Disharmony Resolution",
    desc: "Harmonizing planetary talisman seals to banish friction, restore trust, and bring lasting peace to matrimonial bonds.",
    badge: "Matrimonial Seals",
    icon: Gem,
    span: "md:col-span-3",
    img: "https://images.unsplash.com/photo-1691712820599-c159327225cb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHw0fHxuaWdodCUyMHNreSUyMGNvc21vcyUyMGNvbnN0ZWxsYXRpb24lMjBzdGFycyUyMGdhbGF4eSUyMG15c3RpY2FsfGVufDB8fHx8MTc5MjUwMzQ2OXww&ixlib=rb-4.1.0&q=85",
  },
];

export const Services = () => (
  <section id="services" data-testid="services-section" className="py-28 sm:py-36">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8 }}
        className="mb-16"
      >
        <p className="text-xs uppercase tracking-[0.35em] text-gold/80 font-mono mb-4">
          The Six Working Arts
        </p>
        <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-parchment max-w-2xl">
          Ritual craft for every domain of mortal struggle
        </h2>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-6 gap-5">
        {SERVICES.map((s, i) => (
          <motion.article
            key={s.id}
            data-testid={`service-card-${s.id}`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: (i % 3) * 0.12 }}
            className={`${s.span} group relative overflow-hidden rounded-2xl border border-gold/20 bg-arcanacard p-8 min-h-[260px] flex flex-col justify-end transition-transform duration-500 hover:scale-[1.015] card-glow`}
          >
            {s.img && (
              <>
                <img
                  src={s.img}
                  alt=""
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover opacity-25 group-hover:opacity-40 transition-opacity duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-arcanacard via-arcanacard/70 to-transparent" />
              </>
            )}
            <div className="relative">
              <span className="inline-block text-[10px] uppercase tracking-[0.3em] font-mono text-gold/70 border border-gold/25 rounded-full px-3 py-1 mb-5">
                {s.badge}
              </span>
              <s.icon
                className="text-gold mb-4 transition-transform duration-500 group-hover:-translate-y-1"
                size={28}
                strokeWidth={1.5}
              />
              <h3 className="font-serif text-xl sm:text-2xl text-parchment mb-3">
                {s.title}
              </h3>
              <p className="text-sm text-parchment-muted leading-relaxed max-w-md">
                {s.desc}
              </p>
            </div>
          </motion.article>
        ))}
      </div>
    </div>
  </section>
);
